import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import {
  createMsrLoan,
  deleteMsrLoan,
  getLoanSubledger,
  getServicingMsr,
  updateMsrLoan,
  updateServicingSettings,
} from 'services'
import { svgLoading } from 'stories/assets'
import { confirm, thousandSeperator } from 'utils'

import { EditMsrLoanDialog } from './EditMsrLoanDialog'
import type { IServicingMsrLoan, IServicingPeriodItem, IServicingSettings } from './types'

const formatAmount = (value?: number | null) => {
  if (value == null || (!value && value !== 0)) return '-'
  return `$${thousandSeperator(value)}`
}

export const ServicingMsr = () => {
  const [isLoading, setLoading] = useState(false)
  const [loans, setLoans] = useState<IServicingMsrLoan[]>([])
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [settings, setSettings] = useState<IServicingSettings | null>(null)
  const [periodItems, setPeriodItems] = useState<IServicingPeriodItem[]>([])
  const [loanOptions, setLoanOptions] = useState<Record<string, string>>({})
  const [editItem, setEditItem] = useState<Partial<IServicingMsrLoan> | null>(null)
  const [settingsDraft, setSettingsDraft] = useState({
    servicingFeeIncome: '',
    msrAmortization: '',
  })
  const [savingSettings, setSavingSettings] = useState(false)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const [result, subledger] = await Promise.all([getServicingMsr(), getLoanSubledger()])
      setLoans(result.msrLoans?.results || [])
      setTotals(result.msrLoans?.totals || {})
      setSettings(result.settings || null)
      setPeriodItems(result.periodItems || [])
      setSettingsDraft({
        servicingFeeIncome: String(result.settings?.servicingFeeIncome ?? ''),
        msrAmortization: String(result.settings?.msrAmortization ?? ''),
      })
      setLoanOptions(
        (subledger.results || []).reduce((acc: Record<string, string>, row: { loanId: string }) => {
          if (row.loanId) acc[row.loanId] = row.loanId
          return acc
        }, {}),
      )
    } catch (error) {
      console.error(error)
      setLoans([])
    }
    setLoading(false)
  }

  const onSave = async (data: Partial<IServicingMsrLoan>) => {
    if (editItem?.id) await updateMsrLoan(editItem.id, data)
    else await createMsrLoan(data)
    setEditItem(null)
    fetch()
  }

  const onDelete = async (item: IServicingMsrLoan) => {
    const ok = await confirm(`Delete MSR record for loan "${item.loanId}"?`)
    if (!ok) return
    setLoading(true)
    await deleteMsrLoan(item.id)
    fetch()
  }

  const onSaveSettings = async () => {
    setSavingSettings(true)
    try {
      await updateServicingSettings({
        servicingFeeIncome: Number(settingsDraft.servicingFeeIncome || 0),
        msrAmortization: Number(settingsDraft.msrAmortization || 0),
      })
      fetch()
    } finally {
      setSavingSettings(false)
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Servicing &amp; MSR
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
        <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditItem({})}>
          <PlusIcon className="w-4 h-4" /> Add
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Loans sold servicing-retained capitalize an MSR asset. Carrying value (capitalized − accumulated amortization)
        ties to GL {settings?.msrGlAccountNo || '1500'}. Servicing fee income ties to GL{' '}
        {settings?.servicingFeeGlAccountNo || '4060'}.
      </p>

      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Servicing-Retained / MSR</h2>
        <div className="relative overflow-x-auto">
          <LayoutLoading show={isLoading} />
          <table className="w-full text-sm text-left text-gray-700 min-w-max">
            <thead className="text-xs uppercase bg-gray-100">
              <tr>
                {[
                  'Loan ID',
                  'UPB Serviced',
                  'Capitalized MSR',
                  'Accum. Amort.',
                  'Carrying Value',
                  `GL ${settings?.msrGlAccountNo || '1500'}`,
                  'Variance',
                  'Action',
                ].map((h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loans.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                    No servicing-retained MSR loans yet.
                  </td>
                </tr>
              )}
              {loans.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{item.loanId}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.upbServiced)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.capitalizedMsr)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.accumAmort)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.carryingValue)}</td>
                  <td className="px-3 py-2 text-right">-</td>
                  <td className="px-3 py-2 text-right">-</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 items-center">
                      <span
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                        onClick={() => setEditItem(item)}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                      <span
                        className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                        onClick={() => onDelete(item)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {loans.length > 0 && (
              <tfoot>
                <tr className="bg-blue-50 font-semibold border-t-2">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.upbServiced)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.capitalizedMsr)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.accumAmort)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.carryingValue)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.glBalance)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.variance)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Servicing Income &amp; Amortization</h2>
        <div className="border border-gray-200 rounded overflow-hidden mb-4 max-w-3xl">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs uppercase bg-slate-700 text-white">
              <tr>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">GL Account</th>
                <th className="px-3 py-2 text-right">GL Balance</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {periodItems.map((item) => (
                <tr key={item.key} className="border-b">
                  <td className="px-3 py-2">{item.label}</td>
                  <td className="px-3 py-2 text-right">
                    {item.key === 'servicingFeeIncome' ? (
                      <input
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-28 text-right"
                        type="number"
                        value={settingsDraft.servicingFeeIncome}
                        onChange={(e) =>
                          setSettingsDraft((prev) => ({ ...prev, servicingFeeIncome: e.target.value }))
                        }
                      />
                    ) : (
                      <input
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-28 text-right"
                        type="number"
                        value={settingsDraft.msrAmortization}
                        onChange={(e) => setSettingsDraft((prev) => ({ ...prev, msrAmortization: e.target.value }))}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">GL {item.glAccountNo}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.glBalance)}</td>
                  <td className="px-3 py-2">
                    <span className={item.ok ? 'text-green-700 font-semibold' : 'text-red-600 font-semibold'}>
                      {item.ok ? 'OK' : 'VARIANCE'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          className="bg-primary text-white rounded px-3 py-1.5 text-sm disabled:opacity-50"
          disabled={savingSettings}
          onClick={onSaveSettings}
        >
          Save Period Amounts
        </button>
      </div>

      {editItem && (
        <EditMsrLoanDialog
          item={editItem}
          loanOptions={loanOptions}
          onClose={() => setEditItem(null)}
          onSave={onSave}
        />
      )}
    </div>
  )
}
